import { DateHelper } from "@/constant/helper.ts";
import { MANAGER_PATH } from "@/constant/url-path.ts";
import { DeleteButton } from "@/view/license/component/DeleteLicenseRegistrationButton.tsx";
import { DetailButton } from "@/view/license/component/DetailLicenseRegistrationButton.tsx";
import { Avatar, Button, Space, TableProps } from "antd";
import { TFunction } from "i18next";
import * as jdenticon from "jdenticon";
import { useNavigate } from "react-router-dom";

export const LicenseRegistrationTableHeader = (
  t: TFunction,
  research: () => void,
): TableProps<LicenseRegistrationItemSearch>["columns"] => {
  return [
    {
      title: t("field.no"),
      dataIndex: "no",
      key: "no",
      align: "center",
      render: (_, _record, index) => <span>{index + 1}</span>,
    },
    {
      title: t("field.productName"),
      dataIndex: "productName",
      key: "productName",
      width: 300,
      render: (_, record) => {
        const { productName, productVersion, productCode } = record as any;
        const name = productName || "";
        const initials = name
          .split(" ")
          .filter((s: string) => s)
          .map((n: string) => n[0])
          .slice(0, 2)
          .join("")
          .toUpperCase();

        const seedRaw = String(productCode ?? productName ?? name ?? "unknown");
        const thumbSize = 40; // pixels for the generated SVG avatar
        const svg = jdenticon.toSvg(seedRaw, thumbSize);
        const avatarDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

        return (
          <ProductNameCell
            productName={productName}
            productVersion={productVersion}
            productCode={productCode}
            avatarDataUrl={avatarDataUrl}
            initials={initials}
          />
        );
      },
    },
    {
      title: t("field.registrationDate"),
      dataIndex: "createdDate",
      key: "createdDate",
      sorter: true,
      showSorterTooltip: false,
      align: "center",
      render: (_, { createdDate }) => {
        return (
          <>
            {createdDate ? (
              <div>{DateHelper.convertDateToStringDate(createdDate)}</div>
            ) : (
              <div>----/--/--</div>
            )}
          </>
        );
      },
    },
    {
      title: t("field.fullName"),
      dataIndex: "fullName",
      key: "fullName",
      render: (_, { firstName, lastName }) => (
        <span className={"whitespace-nowrap"}>
          {firstName + " " + lastName}
        </span>
      ),
    },
    {
      title: t("field.expirationDate"),
      dataIndex: "expirationDate",
      key: "expirationDate",
      sorter: true,
      showSorterTooltip: false,
      align: "center",
      render: (_, { expirationDate }) => {
        return (
          <>
            {expirationDate ? (
              <div>{DateHelper.convertDateToStringDate(expirationDate)}</div>
            ) : (
              <div>----/--/--</div>
            )}
          </>
        );
      },
    },
    {
      title: t("field.action"),
      key: "action",
      align: "center",
      render: (_, record) => {
        return (
          <Space size="middle">
            <DetailButton record={record} />
            <DeleteButton record={record} research={research} />
          </Space>
        );
      },
    },
  ];
};

const ProductNameCell = ({
  productName,
  productVersion,
  productCode,
  avatarDataUrl,
  initials,
}: {
  productName?: string | null;
  productVersion?: string | null;
  productCode?: string | null;
  avatarDataUrl: string;
  initials: string;
}) => {
  const navigation = useNavigate();
  const displayName = productName
    ? productVersion
      ? `${productName} - (${productVersion})`
      : productName
    : "-";

  const onClick = () => {
    if (!productCode) return;
    const params = new URLSearchParams();
    params.append("productCode", String(productCode));
    navigation(`${MANAGER_PATH.PRODUCT.EDIT}?${params.toString()}`);
  };

  return (
    <div className={"flex items-center"}>
      <Avatar
        size={40}
        src={avatarDataUrl}
        style={{ backgroundColor: "#87d068" }}
      >
        {initials || "?"}
      </Avatar>
      <Button type="link" onClick={onClick} style={{ marginLeft: 8 }}>
        {displayName}
      </Button>
    </div>
  );
};
